// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  BillingPlanSortProperty,
  BillingPlanStatus,
  billingPlanListingSchema,
} from '@/billing/api/types';
import { BillingFrequency } from '@/constants/billing-frequency';
import { Sort } from '@/constants/sort';
import { z } from 'zod';

const billingPlansResponseSchema = z.object({
  data: billingPlanListingSchema,
  metadata: responseMetadataSchema,
});

export interface BillingPlanFilter {
  pageSize: number;
  pageNumber?: number;
  sort?: Sort<BillingPlanSortProperty>;
  search?: string;
  status?: BillingPlanStatus;
  customerKeys?: string[];
  projectKeys?: string[];
  // Multi-select schedule type filter. PERIODIC or MILESTONE.
  chargeSchedules?: ('PERIODIC' | 'MILESTONE')[];
  // Multi-select charge frequency filter (MONTHLY, QUARTERLY, etc.).
  chargeFrequencies?: BillingFrequency[];
  activationDateStart?: string;
  activationDateEnd?: string;
  amountMin?: number;
  amountMax?: number;
}

export function serializeBillingPlanFilter(filter?: BillingPlanFilter): string {
  if (!filter) {
    return '';
  }
  const params = new URLSearchParams();
  params.append('limit', filter.pageSize.toString());
  if (filter.pageNumber !== undefined) {
    params.append('page', filter.pageNumber.toString());
  }
  if (filter.sort) {
    params.append('sortBy', filter.sort.property);
    params.append('sortOrder', filter.sort.direction);
  }
  if (filter.search) {
    params.append('search', filter.search);
  }
  if (filter.status) {
    params.append('status', filter.status);
  }
  if (filter.customerKeys && filter.customerKeys.length > 0) {
    [...filter.customerKeys].sort().forEach((key) => {
      params.append('customerKey', key);
    });
  }
  if (filter.projectKeys && filter.projectKeys.length > 0) {
    [...filter.projectKeys].sort().forEach((key) => {
      params.append('projectKey', key);
    });
  }
  if (filter.chargeSchedules && filter.chargeSchedules.length > 0) {
    [...filter.chargeSchedules].sort().forEach((s) => {
      params.append('chargeSchedule', s);
    });
  }
  if (filter.chargeFrequencies && filter.chargeFrequencies.length > 0) {
    [...filter.chargeFrequencies].sort().forEach((f) => {
      params.append('chargeFrequency', f);
    });
  }
  if (filter.activationDateStart) {
    params.append('activationDateStart', filter.activationDateStart);
  }
  if (filter.activationDateEnd) {
    params.append('activationDateEnd', filter.activationDateEnd);
  }
  if (filter.amountMin !== undefined) {
    params.append('amountMin', filter.amountMin.toString());
  }
  if (filter.amountMax !== undefined) {
    params.append('amountMax', filter.amountMax.toString());
  }
  return params.toString();
}

export async function getBillingPlans(
  filter: BillingPlanFilter = { pageSize: 20 }
) {
  const serializedFilter = serializeBillingPlanFilter(filter);
  const url = serializedFilter
    ? `/v1/billing-plan?${serializedFilter}`
    : '/v1/billing-plan';
  const response = await api.get(url);
  const result = await parseResponse(
    billingPlansResponseSchema,
    response.data,
    {
      schemaName: 'BillingPlans',
      endpoint: url,
    }
  );
  return result.data;
}
